"use client"

/**
 * Pricing Cards Container
 *
 * Shared container component for displaying pricing plan cards.
 * Used by both the landing page and dashboard upgrade modal.
 *
 * Features:
 * - Automatic plan filtering for upgrades (excludes current tier and below)
 * - Size variants (default for landing, compact for modal)
 * - Discount support from promo codes
 * - Vertical stack on mobile, grid on desktop
 */

import React, { useMemo, memo } from "react"
import { T } from "gt-next"
import PricingCard, { type PricingPlan } from "./PricingCard"
import { PRICING_PLANS, getFilteredPlans } from "./plans-data"

interface PricingCardsProps {
    /** Current billing period */
    activePeriod: "Monthly" | "Yearly"
    /** Size variant - "compact" for modal use */
    size?: "default" | "compact"
    /** Current plan name - filters out this tier and below for upgrades */
    currentPlan?: string
    /** Callback when a plan is selected (enables modal mode) */
    onPlanSelected?: (plan: PricingPlan) => void
    /** Optional discount percentage from promo code */
    discountPercent?: number
    /** Show loading state on a specific plan */
    loadingPlan?: string
    /** Override plans array (for custom filtering) */
    plans?: PricingPlan[]
    /** Additional CSS classes for the container */
    className?: string
    /** Hide the struck-through renewal price on each card (public pricing page) */
    hideRenewalPrice?: boolean
}

export default memo(function PricingCards({ activePeriod, size = "default", currentPlan, onPlanSelected, discountPercent = 0, loadingPlan, plans: customPlans, className = "", hideRenewalPrice = false }: PricingCardsProps) {
    // Filter plans based on current tier (for upgrade modal)
    const displayPlans = useMemo(() => {
        if (customPlans) return customPlans
        return currentPlan ? getFilteredPlans(currentPlan) : PRICING_PLANS
    }, [customPlans, currentPlan])

    // Check if any displayed plan is featured (for vertical alignment compensation)
    const hasFeaturedPlan = displayPlans.some(p => p.featured)

    // Determine context based on whether callback is provided
    const context = onPlanSelected ? "modal" : "landing"

    // Grid layout - 3 cols on desktop, 2 on tablet, 1 on mobile
    // For upgrade modal with fewer plans, adjust grid
    const gridCols = displayPlans.length === 1 ? "grid-cols-1 max-w-md mx-auto" : displayPlans.length === 2 ? "grid-cols-1 md:grid-cols-2 max-w-2xl mx-auto" : "grid-cols-1 md:grid-cols-3"

    if (displayPlans.length === 0) {
        return <div className="py-8 text-center text-black/50"><T>You're on the highest tier. No upgrades available.</T></div>
    }

    return (
        <div className={`grid ${gridCols} gap-3 px-4 pb-6 md:gap-4 md:px-0 md:pb-0 ${className}`}>
            {displayPlans.map((plan) => (
                <div key={plan.name} className={`h-full ${hasFeaturedPlan && !plan.featured ? "md:pt-9" : ""}`}>
                    <PricingCard plan={plan} activePeriod={activePeriod} variant={plan.featured ? "featured" : "light"} size={size} context={context} onPlanSelected={onPlanSelected} discountPercent={discountPercent} isLoading={loadingPlan === plan.name} hideRenewalPrice={hideRenewalPrice} />
                </div>
            ))}
        </div>
    )
})

/**
 * Re-export types and data for convenience
 */
export { PRICING_PLANS, getFilteredPlans } from "./plans-data"
export type { PricingPlan }
